import React, { useCallback, useRef, useState, useEffect } from 'react';
import {
  View,
  FlatList,
  StyleSheet,
  ActivityIndicator,
  RefreshControl,
  Dimensions,
  Image,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { Text, Theme } from '../../components/ui';
import InsightCard from './components/InsightCard';
import { useInsightsFeed } from './hooks/useInsightsFeed';
import { InsightsStackParamList } from '../../navigation/InsightsStackNavigator';
import { useNavigationLogging } from '../../hooks/useNavigationLogging';
import { loggingService } from '../../services/loggingService';

const { height: SCREEN_HEIGHT } = Dimensions.get('window');

// Temporary background images from Figma (until we generate per-insight images)
const BACKGROUND_IMAGES = [
  'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080',
  'https://images.unsplash.com/photo-1470252649378-9c29740c9fa8?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080',
  'https://images.unsplash.com/photo-1519681393784-d120267933ba?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080',
  'https://images.unsplash.com/photo-1495195134817-aeb325a55b65?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080',
  'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080',
  'https://images.unsplash.com/photo-1475924156734-496f6cac6ec1?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080',
];

/**
 * Insights Feed Screen
 * TikTok-style vertical scrolling feed of insights
 * Full-screen cards with snap-to-item paging
 */
const InsightsFeedScreen: React.FC = () => {
  useNavigationLogging('InsightsFeedScreen');
  const navigation = useNavigation<StackNavigationProp<InsightsStackParamList>>();
  const flatListRef = useRef<FlatList>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const {
    items,
    loading,
    error,
    hasMore,
    refresh,
    loadMore,
    onIndexChange,
  } = useInsightsFeed();

  /**
   * Handle Read action - navigate to reader
   */
  const handleRead = useCallback((index: number) => {
    const item = items[index];
    if (!item) return;

    // Log analytics
    loggingService.logInsightOpenFromFeed(item.insight.id, index, 'read');

    // Navigate to BookReader with actual insight objects
    // This avoids the need to re-fetch from Firestore
    const insights = items.map(i => i.insight);

    (navigation as any).navigate('BookReader', {
      insights, // Pass actual insights, not just IDs
      currentIndex: index,
      fromRoute: 'InsightsFeed',
      autoPlayAudio: false,
      isMixedInsights: true, // Insights from different books
    });
  }, [items, navigation]);

  /**
   * Handle Listen action - navigate to reader with auto-play
   */
  const handleListen = useCallback((index: number) => {
    const item = items[index];
    if (!item) return;

    // Log analytics
    loggingService.logInsightOpenFromFeed(item.insight.id, index, 'listen');

    // Navigate to BookReader with actual insight objects
    const insights = items.map(i => i.insight);

    (navigation as any).navigate('BookReader', {
      insights, // Pass actual insights, not just IDs
      currentIndex: index,
      fromRoute: 'InsightsFeed',
      autoPlayAudio: true, // Signal to start audio automatically
      isMixedInsights: true, // Insights from different books
    });
  }, [items, navigation]);

  /**
   * Prefetch image for next item
   */
  const prefetchNextImage = useCallback((index: number) => {
    const nextIndex = index + 1;
    if (nextIndex < items.length) {
      const nextBackgroundImage = getBackgroundImage(nextIndex);
      Image.prefetch(nextBackgroundImage).catch(err => {
        // Non-critical, ignore errors
        console.warn('Image prefetch failed:', err);
      });
    }
  }, [items.length]);

  /**
   * Track current visible item for analytics and prefetching
   */
  const onViewableItemsChangedRef = useRef(({ viewableItems }: any) => {
    // Initial ref value - will be updated by useEffect
  });
  
  // Update the ref callback when dependencies change
  useEffect(() => {
    onViewableItemsChangedRef.current = ({ viewableItems }: any) => {
      if (viewableItems.length > 0) {
        const index = viewableItems[0].index;
        if (index !== null && index !== currentIndex) {
          setCurrentIndex(index);
          
          // Notify hook about index change (for seen tracking + prefetch)
          onIndexChange(index);
          
          // Prefetch next image
          prefetchNextImage(index);
          
          // Log analytics
          const item = items[index];
          if (item) {
            loggingService.logInsightsFeedView(item.insight.id, index);
          }
        }
      }
    };
  }, [currentIndex, onIndexChange, prefetchNextImage, items]);
  
  const onViewableItemsChanged = useCallback(
    (info: any) => onViewableItemsChangedRef.current(info),
    []
  );

  const viewabilityConfig = useRef({
    itemVisiblePercentThreshold: 50,
  }).current;

  /**
   * Get background image for an item (cycle through array)
   */
  const getBackgroundImage = (index: number) => {
    return BACKGROUND_IMAGES[index % BACKGROUND_IMAGES.length];
  };

  /**
   * Initialize index on mount (for sliding window)
   */
  const hasInitialized = useRef(false);
  
  useEffect(() => {
    if (items.length > 0 && !hasInitialized.current) {
      hasInitialized.current = true;
      onIndexChange(0);
      prefetchNextImage(0);
    }
  }, [items.length, onIndexChange, prefetchNextImage]); // Only run when items first appear

  /**
   * Render individual insight card
   */
  const renderItem = useCallback(({ item, index }: any) => {
    return (
      <InsightCard
        insight={item.insight}
        book={item.book}
        onRead={() => handleRead(index)}
        onListen={() => handleListen(index)}
        backgroundImage={getBackgroundImage(index)}
      />
    );
  }, [handleRead, handleListen]);

  /**
   * Loading state
   */
  if (loading && items.length === 0) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color={Theme.colors.primary} />
        <Text style={styles.loadingText}>Loading steps...</Text>
      </View>
    );
  }

  /**
   * Error state
   */
  if (error && items.length === 0) {
    return (
      <View style={styles.centerContainer}>
        <Text style={styles.errorText}>Unable to load steps</Text>
        <Text style={styles.errorSubtext}>{error}</Text>
      </View>
    );
  }

  /**
   * Empty state
   */
  if (items.length === 0) {
    return (
      <View style={styles.centerContainer}>
        <Text style={styles.emptyText}>No steps available</Text>
        <Text style={styles.emptySubtext}>Check back later for new content</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <FlatList
        ref={flatListRef}
        data={items}
        renderItem={renderItem}
        keyExtractor={(item, index) => `${item.insight.id}-${index}`}
        pagingEnabled
        showsVerticalScrollIndicator={false}
        snapToAlignment="start"
        snapToInterval={SCREEN_HEIGHT}
        decelerationRate="fast"
        onViewableItemsChanged={onViewableItemsChanged}
        viewabilityConfig={viewabilityConfig}
        onEndReached={hasMore ? loadMore : undefined}
        onEndReachedThreshold={0.5}
        refreshControl={
          <RefreshControl
            refreshing={false}
            onRefresh={refresh}
            tintColor="#fff"
            titleColor="#fff"
          />
        }
        getItemLayout={(data, index) => ({
          length: SCREEN_HEIGHT,
          offset: SCREEN_HEIGHT * index,
          index,
        })}
        removeClippedSubviews={true}
        maxToRenderPerBatch={1}
        windowSize={2}
        initialNumToRender={1}
        updateCellsBatchingPeriod={100}
      />

      {/* Progress Indicator Dots - Hidden for infinite scroll feed */}
      {/* Uncomment if needed for non-paginated insight views */}
      {/* {items.length > 1 && !hasMore && (
        <View style={styles.progressContainer}>
          {items.slice(0, Math.min(items.length, 10)).map((_, index) => (
            <View
              key={index}
              style={[
                styles.progressDot,
                index === currentIndex && styles.progressDotActive,
              ]}
            />
          ))}
        </View>
      )} */}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Theme.colors.background,
    padding: Theme.spacing.xl,
  },
  loadingText: {
    fontSize: 16,
    fontFamily: Theme.typography.fontFamily.medium,
    color: Theme.colors.textSecondary,
    marginTop: Theme.spacing.md,
  },
  errorText: {
    fontSize: 18,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: Theme.colors.textPrimary,
    textAlign: 'center',
    marginBottom: Theme.spacing.sm,
  },
  errorSubtext: {
    fontSize: 14,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textSecondary,
    textAlign: 'center',
  },
  emptyText: {
    fontSize: 18,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: Theme.colors.textPrimary,
    textAlign: 'center',
    marginBottom: Theme.spacing.sm,
  },
  emptySubtext: {
    fontSize: 14,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textSecondary,
    textAlign: 'center',
  },
  progressContainer: {
    position: 'absolute',
    right: 16,
    top: '50%',
    transform: [{ translateY: -50 }],
    gap: 12,
    zIndex: 10,
  },
  progressDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: 'rgba(255, 255, 255, 0.4)',
  },
  progressDotActive: {
    width: 8,
    height: 32,
    borderRadius: 4,
    backgroundColor: '#fff',
  },
});

export default InsightsFeedScreen;

